<template>
  <div class="region-environments-page">
    <div class="header row">
      <div>
        <HKLGrayHint
          v-if="latestUpdateOn"
        >
          {{ latestUpdateOn }}
        </HKLGrayHint>
        <p
          v-if="showRestartHint"
        >
          {{ $t('hint.regionConfig.requireRestart') }}
        </p>
      </div>
      <div
        v-if="canEdit"
        class="btn-group"
      >
        <HKLUploadButton
          v-if="!isEdit"
          @importFile="(result) => importJson(result)"
        />
        <HKLButton
          v-if="!isEdit"
          color="orange"
          action="export"
          @click="() => exportJson()"
        />
        <HKLButton
          v-if="!isEdit"
          color="orange"
          action="edit"
          @click="() => changeEdit()"
        />
        <HKLButton
          v-if="isEdit"
          color="darkblue"
          action="cancel"
          @click="() => cancelEdit()"
        />
        <HKLButton
          v-if="isEdit"
          color="orange"
          action="save"
          @click="() => saveEnv()"
        />
      </div>
    </div>
    <Collapse
      v-for="(type, key) in list"
      :key="`env-list-${key}`"
      :title="$t(`settings.regionConfig.moduleTitle.${key}`)"
      :expand="false"
      class="region-environments-collapse"
    >
      <template slot="content">
        <HKLForm
          v-for="(item, itemType) in type"
          :key="`env-form-${itemType}`"
          class="env-form"
        >
          <div
            v-if="itemType === REGION_ENV_TYPE.JOBS && Object.keys(item).length"
            class="environment-label-jobs-title"
          >
            {{ $t('settings.regionConfig.jobs') }}
          </div>
          <div
            v-for="(content, index) in item"
            :key="`list-${index}`"
          >
            <template
              v-if="isFormatByBuilding(content)"
            >
              <div
                v-for="(buildingContent, buildingIndex) in content.value"
                :key="`special-with-building-${buildingIndex}`"
                class="special-with-building-list"
              >
                <div
                  class="list-title title-with-building small-4"
                >
                  <div class="list-title-label small-6">
                    {{ buildingIndex === 0 ? index : '' }}
                  </div>

                  <HKLMultiSelect
                    v-if="isEdit"
                    v-model="buildingContent.building"
                    class="building-selection"
                    placeholder="Building"
                    :options="buildingOptions"
                  />
                  <div v-else>
                    {{ buildingContent.building.join(', ') }}
                  </div>
                </div>
                <HKLTextarea
                  v-if="isEdit"
                  v-model="buildingContent.value"
                  class="small-7"
                />
                <div
                  v-if="isEdit"
                  class="small-1"
                >
                  <div
                    v-if="(content.value || []).length>1"
                    class="remove-btn"
                    @click="() => deleteRow({itemModule: key, itemType, itemKey: index, index: buildingIndex})"
                  >
                    <i
                      class="fa fa-times fa-lg"
                    />
                  </div>
                </div>

                <p
                  v-if="!isEdit"
                  class="content-value"
                >
                  {{ buildingContent.value }}
                </p>
              </div>
              <div class="row">
                <div class="list-title title-with-building small-4" />

                <HKLButton
                  v-if="isEdit"
                  class="add-btn"
                  action="addPlus"
                  @click="() => addRow({itemModule: key, itemType, itemKey: index}, {
                    building: [],
                    value: '',
                  })"
                />
              </div>
            </template>
            <template v-else-if="needTemplateNEntitlement(content)">
              <TemplateEntitlementList
                :isEdit="isEdit"
                :contentValue.sync="content.value"
                :index="index"
                :templates="wpTemplates"
                :entitlements="entitlements"
                :getEntitlementSelections="getEntitlementSelections"
                :formatPermStr="formatPermStr"
                :templateModule="key"
                :onDelete="(templateIndex) => deleteRow({itemModule: key, itemType, itemKey: index, index: templateIndex}, templatePermDefault)"
                :onAdd="() => addRow({itemModule: key, itemType, itemKey: index}, templatePermDefault)"
              />
            </template>

            <div
              v-else
              class="environment-list"
            >
              <div class="list-title small-4">
                {{ index }}
              </div>

              <HKLTextarea
                v-if="isEdit"
                v-model="content.value"
                v-autosize
                class="content-value small-7"
                :disabled="content.readOnly"
              />
              <p
                v-else
                class="content-value"
              >
                {{ content.value }}
              </p>
            </div>
          </div>
        </HKLForm>
      </template>
    </Collapse>
  </div>
</template>

<script>
import { unref, ref, onMounted, computed } from 'vue-demi'
import moment from 'moment'
import lodash from 'lodash'
import Collapse from '@/controls/Collapse'
import HKLForm from '@/controls/Form/HKLForm'
import notification from '@/utils/notification'
import HKLTextarea from '@/controls/Input/HKLTextarea.vue'
import HKLButton from '@/controls/Button/HKLButton.vue'
import i18n from '@/utils/i18n'
import HKLGrayHint from '@/controls/HKLGrayHint'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import { useStoreGetters } from '@/store'
import { REGION_ENV_TYPE } from '@/modules/settings/engineers/consts'
import { getRegionalEnvs, updateReginalEnvs, exportContent, getTemplatesNEntitlements } from '@/api/settings/request'
import { aclHelper, aclPermsType, ActionNameI18nMap, ResourceNameMap } from '@/utils/aclHelper'
import HKLUploadButton from '@/controls/Button/HKLUploadButton'
import { getLastUpdatedByOn, isEnabledFeature, splitListString } from './utils'
import TemplateEntitlementList from './TemplateEntitlementList.vue'
export default {
  components: {
    Collapse,
    HKLForm,
    HKLTextarea,
    HKLButton,
    HKLGrayHint,
    HKLMultiSelect,
    HKLUploadButton,
    TemplateEntitlementList,
  },
  setup () {
    let list = ref([])
    const history = ref({})
    let isEdit = ref(false)
    let wpTemplates = ref([])
    let entitlements = ref([])
    const restartTime = ref('')
    const needRestartAfter = ref('')
    const templatePermDefault = { template: '', permList: [] }
    const { allBuildings } = useStoreGetters('buildings', ['allBuildings'])
    const { currentRegion } = useStoreGetters('regions', ['currentRegion'])
    const buildingOptions = unref(allBuildings).map(building => building.code)
    const saveEnv = async () => {
      const result = flattenList()
      try {
        await updateReginalEnvs(result)
        notification.fireNotification(true, i18n.t('success'))
      } catch (err) {
        notification.handleResponseError(err)
        throw err
      }
      await getData()
      changeEdit()
    }
    const formatEnvByBuilding = (list = {}, key) => {
      let result = []
      const defaultValue = [{
        building: [],
        value: '',
      }]

      Object.keys(list).map(listKey => {
        const buildingList = listKey.split(`${[key]}_`)[1]
        const buildingArray = splitListString(buildingList, '_')
        if (buildingArray.some(b => !unref(buildingOptions).includes(b))) { return }
        result.push({
          building: buildingArray,
          value: list[listKey],
        })
      })

      return result.length ? result : defaultValue
    }
    const formatEnvByTempNPerm = (list = {}, key) => {
      let result = []

      Object.keys(list).map(listKey => {
        const templateName = listKey.split(`${[key]}_`)[1]
        const templateFromList = unref(wpTemplates).find(t => t.name === templateName)

        if (!templateFromList) return
        result.push({
          template: templateFromList.value,
          permList: revertPermStr(list[listKey]),
        })
      })

      return result.length ? result : [templatePermDefault]
    }
    const revertPermStr = (formattedPermission = '') => {
      return formattedPermission
        .split('; ')
        .map(permission => {
          const [resource, action] = permission.split(':')
          const originalResource = (Object.entries(ResourceNameMap).find(([, val]) => i18n.t(val) === resource) || [])[0]
          const originalAction = (Object.entries(ActionNameI18nMap[originalResource] || {}).find(([, val]) => i18n.t(val) === action) || [])[0]
          if (!originalResource || !originalAction || !isEnabledFeature(originalResource)) {
            return null
          }
          return `${originalResource}:${originalAction}`
        }).filter(perm => !!perm)
    }

    const importJson = (result) => {
      const data = {...(result.dbEnvs || {}), ...(result.regionalEnvs || {})}
      const listValue = unref(list)
      const listKey = Object.keys(listValue)
      listKey.map((module) => {
        const flattenList = Object.assign({}, listValue[module].NORMAL, listValue[module].JOBS)
        Object.entries(flattenList).forEach(([key, val]) => {
          if (val.readOnly) {
            return
          }
          if (val.isByBuilding) {
            const byBuildingList = Object.keys(data).filter(item => item.startsWith(key)).reduce((res, objKey) => {
              res[objKey] = data[objKey]
              return res
            }
            , {})
            val.value = formatEnvByBuilding(byBuildingList, key)
            return
          }
          if (val.needEntitlement) {
            const byPermList = Object.keys(data).filter(item => item.startsWith(key)).reduce((res, objKey) => {
              res[objKey] = data[objKey]
              return res
            }
            , {})
            val.value = formatEnvByTempNPerm(byPermList, key)
            return
          }
          val.value = data[key] || ''
        })
      })
      changeEdit()
    }

    const exportJson = async () => {
      const request = {
        filename: `${i18n.t('settings.regionConfig.env')}_${unref(currentRegion).name}.json5`,
        content: flattenList(true),
      }

      await exportContent(request)
    }
    const latestUpdateOn = computed(() => {
      return getLastUpdatedByOn(unref(history) || {})
    })

    const handleTemplateChange = (templateIndex, index, key, itemType) => (newKey) => {
      const content = unref(list)[key][itemType][index]
      if (!content || !content.value) return
      if (!content.value[templateIndex]) content.value[templateIndex] = {}
      content.value[templateIndex].template = newKey
    }

    const formatPermStr = (templatePerm = {}) => {
      let result = []
      result = (templatePerm.permList || []).map(perm => {
        if (perm && perm.length) {
          const [resource, ...actionArray] = (perm).split(':')
          if (isEnabledFeature(resource)) {
            const resourceName = (ResourceNameMap || {})[resource] || resource
            const actionName = (ActionNameI18nMap[resource] || {})[actionArray.join(':')] || actionArray.join(':')
            return `${i18n.t(resourceName)}:${i18n.t(actionName)}`
          }
        }
      }).filter(perm => !!perm)
      return result.join('; ')
    }

    const changeEdit = () => {
      isEdit.value = !isEdit.value
    }

    const cancelEdit = async () => {
      await getData()
      changeEdit()
    }

    const addRow = (path, defaultValue) => {
      const {itemModule, itemType, itemKey} = path
      unref(list)[itemModule][itemType][itemKey].value.push(lodash.cloneDeep(defaultValue))
    }

    const deleteRow = (path, defaultValue) => {
      const {itemModule, itemType, itemKey, index} = path
      const content = unref(list)[itemModule][itemType][itemKey]
      if ((content.value || []).length === 1 && defaultValue) {
        content.value = [Object.assign({}, lodash.cloneDeep(defaultValue))]
      } else {
        content.value.splice(index, 1)
      }
    }

    const isFormatByBuilding = (content) => {
      return content.isByBuilding
    }

    const needTemplateNEntitlement = (content) => {
      return content.needEntitlement && content.needTemplate
    }
    const getEntitlementSelections = (templatePerm) => {
      if (!templatePerm.permList) return []
      return templatePerm.permList.filter(item => !!item)
    }
    const handleEntitlementChange = (templateIndex, index, key, itemType) => (newKey) => {
      // Find the correct content object
      const content = unref(list)[key][itemType][index]

      if (!content || !content.value) return
      const value = Array.isArray(newKey) ? newKey : []
      if (!content.value[templateIndex]) content.value[templateIndex] = {}
      content.value[templateIndex].permList = value
    }

    const showRestartHint = computed(() => {
      const systemRestartAt = unref(restartTime)
      const needResAfter = unref(needRestartAfter)
      if (systemRestartAt && needResAfter) {
        const updatedAt = moment(needResAfter)
        const latestRestartTime = moment(systemRestartAt)
        return latestRestartTime.isBefore(updatedAt)
      }
      return false
    })

    const formatStr = (str) => {
      if (typeof str === 'string') {
        return str.trim().replace(/[\n\r\t]/g, ' ')
      } else {
        return str
      }
    }
    const canEdit = computed(() => {
      return aclHelper.getAccess(aclPermsType.settings.updateRegional)
    })

    const flattenList = (isExport = false) => {
      const listValue = unref(list)
      const listKey = Object.keys(listValue)
      const result = listKey.reduce((result, module) => {
        const flattenList = Object.assign({}, listValue[module].NORMAL, listValue[module].JOBS)
        Object.entries(flattenList).forEach(([key, val]) => {
          let value = formatStr(val.value) || val.default
          if (typeof value === 'boolean') {
            value = value.toString()
          }

          if (val.readOnly && isExport) {
            return
          }
          if (value || isExport) {
            const defaultFormatResult = {[key]: formatStr(value || '')}
            const formatDbResult = defaultFormatResult
            if (val.isDb) {
              if (needTemplateNEntitlement(val)) {
                if (isExport) {
                  (value || []).map(templateObj => {
                    if (!templateObj.template) return

                    const formatEnvResult = {[`${key}_${i18n.t(`emailTemplate.${module}.${templateObj.template}`)}`]: formatPermStr(templateObj)}
                    Object.assign(result.dbEnvs, formatEnvResult)
                  })
                } else {
                  const filterArray = value.filter(t => !!t && t.template)
                  const formattedValue = filterArray.length ? filterArray : [templatePermDefault]
                  Object.assign(result.dbEnvs, {[key]: formattedValue})
                }
                // Handle specific key transformation
              } else {
                Object.assign(result.dbEnvs, formatDbResult)
              }
            } else {
              if (flattenList[key].isByBuilding) {
                const byBuildingList = val.value
                if (Array.isArray(byBuildingList)) {
                  byBuildingList.map(listItem => {
                    if (listItem.value && listItem.building.length) {
                      const formatKey = `${[key]}_${listItem.building.join('_')}`
                      const formatEnvResult = { [formatKey]: formatStr(listItem.value) }
                      Object.assign(result.regionalEnvs, formatEnvResult)
                    }
                  })
                }
              } else {
                const formatEnvResult = defaultFormatResult
                Object.assign(result.regionalEnvs, formatEnvResult)
              }
            }
          }
        })
        return result
      }, {regionalEnvs: {}, dbEnvs: {}})
      return result
    }
    const getData = async () => {
      const response = await getRegionalEnvs()
      list.value = response.data
      history.value = response.history || {}
      restartTime.value = response.restartAt
      needRestartAfter.value = response.needRestartAfter
    }
    const formatPermToOptions = (input) => {
      const formattedArray = []
      Object.keys(input).forEach((key) => {
        const permissions = input[key]
        const name = (i18n.t((ResourceNameMap || {})[key]) || key).split('/').join(' / ')
        const options = permissions.map((permission) => ({
          name: `${name}:${(i18n.t((ActionNameI18nMap[key] || {})[permission]) || permission).split('/').join(' / ')}`,
          value: `${key}:${permission}`,
        }))
        formattedArray.push({
          key: key,
          name, // Use i18n for the name if available
          options: options,
        })
      })

      return formattedArray
    }
    const getTemplateNEntitlement = async () => {
      const response = await getTemplatesNEntitlements()
      wpTemplates.value = (response.templatesList || []).map((item, key) => {
        return { name: i18n.t(`emailTemplate.work_permit.${item}`), value: item }
      })
      const formatPerms = formatPerm(response.fullEntitlements)
      const filteredPerms = Object.keys(formatPerms)
        .filter((resource) => isEnabledFeature(resource))
        .reduce((acc, resource) => {
          acc[resource] = formatPerms[resource]
          return acc
        }, {})
      entitlements.value = formatPermToOptions(filteredPerms) || []
    }

    const formatPerm = (perms) => {
      const formattedPerms = {}

      Object.keys(perms).forEach((key) => {
        const permissions = perms[key]
        formattedPerms[key] = []

        Object.keys(permissions).forEach((action) => {
          const subActions = permissions[action]

          // Formatting for other sub-actions
          subActions.forEach((subAction) => {
            formattedPerms[key].push(`${action}:${subAction}`)
          })
        })
      })

      return formattedPerms
    }
    onMounted(async () => {
      await getData()
      await getTemplateNEntitlement()
    })
    return {
      list,
      saveEnv,
      isEdit,
      templatePermDefault,
      history,
      latestUpdateOn,
      changeEdit,
      isFormatByBuilding,
      needTemplateNEntitlement,
      wpTemplates,
      entitlements,
      buildingOptions,
      addRow,
      deleteRow,
      showRestartHint,
      cancelEdit,
      REGION_ENV_TYPE,
      canEdit,
      importJson,
      exportJson,
      formatPermStr,
      handleTemplateChange,
      getEntitlementSelections,
      handleEntitlementChange,
      TemplateEntitlementList,

    }
  },

}

</script>

<style lang="scss" scoped>
.header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.region-environments-collapse {
  margin-bottom: 20px;
  .env-form {
    margin-bottom: 20px;
    margin-left: 20px;
  }
  .environment-label-jobs-title {
    font-weight: bold;
    font-size:16px;
  }
  .environment-list, .special-with-building-list {
  display: flex;
  margin-top: 10px;
  margin-right: 10px;
}

.list-title {
  margin-right: 10px;
  word-break: break-word;

}

.special-with-building-content-value {
  word-break: break-word;
  display: flex;
}

.title-with-building{
  display: flex
}

.content-value {
  word-break: break-word;

}

.btn-group {
  display: flex;
}

.add-btn {
  text-align: left;
  text-decoration: underline;
  font-style: italic;
  cursor: pointer;
  margin-left: 10px;
  }

.remove-btn {
    cursor: pointer;
    font-size: 14px;
    margin-left: 10px;
    margin-top: 8px;
  }

  .list-title-label {
    margin-right: 10px;
  }

  textarea {
    margin-left: 20px;
  }
}

</style>
